/**
 * Session Refresh — Centralized token refresh + session lifecycle management.
 *
 * WHY THIS EXISTS:
 * Multiple API clients (api-client.ts, typed-client.ts, client.ts) must handle
 * 401 responses consistently. Without centralization each client duplicated (or
 * worse, omitted) session-expiry logic, leading to:
 *   - api-client.ts: NO 401 handling — user saw a raw error instead of login redirect
 *   - typed-client.ts: NO 401 handling — same problem
 *   - client.ts: fired session-expired immediately on 401, never attempted token refresh
 *
 * This module centralizes four concerns:
 *
 * 1. **Token refresh with deduplication** — only one `refreshSession()` call at
 *    a time regardless of how many concurrent 401s arrive.
 * 2. **Session-expired event** — one-shot CustomEvent consumed by ClientShell to
 *    redirect to /login.
 * 3. **Proactive health check** — validates session when a backgrounded tab
 *    becomes visible, refreshing preemptively before the first API call.
 * 4. **Cross-tab idle coordination** — localStorage timestamp so that activity
 *    in any tab prevents idle-timeout logout in other tabs.
 *
 * ARCHITECTURE:
 * All API clients call `handleUnauthorized()` on 401.  It attempts a single
 * deduplicated refresh → returns the new access token (caller retries) or null
 * (session is gone, event fired, ClientShell redirects to /login).
 */

import { getSupabaseBrowserClient } from '@/lib/supabase/client';

// ─── Events ──────────────────────────────────────────────────────────────────

export const SESSION_EXPIRED_EVENT = 'virza:session-expired';
export const WORKSPACE_INVALID_EVENT = 'virza:workspace-invalid';
export const BILLING_LIMIT_EVENT = 'virza:billing-limit-exceeded';
export const PERMISSIONS_CHANGED_EVENT = 'virza:permissions-changed';

// ─── Session Termination ────────────────────────────────────────────────────

import { ProblemTypes, matchProblemType } from '@/contracts';

/**
 * Reason the session was terminated. Carried in the SESSION_EXPIRED_EVENT
 * detail so ClientShell can show a context-appropriate message.
 *
 * Derived from ProblemTypes so slug renames are caught at compile time.
 */
export type SessionTerminationReason =
  | typeof ProblemTypes.SESSION_EXPIRED
  | typeof ProblemTypes.ACCOUNT_DELETED
  | typeof ProblemTypes.ACCOUNT_SUSPENDED;

/**
 * Session-level 403 problem type slugs that trigger sign-out.
 *
 * NOTE: SESSION_SUSPICIOUS is intentionally excluded — suspicious sessions
 * use soft enforcement (reads allowed, writes blocked with a challenge
 * banner) instead of forcing sign-out. This matches Google/GitHub/Slack.
 */
const SESSION_FORBIDDEN_SLUGS = [
  ProblemTypes.ACCOUNT_DELETED,
  ProblemTypes.ACCOUNT_SUSPENDED,
] as const;

/**
 * Classify a 403 response as a session-level termination event.
 *
 * Shared between api-client and typed-client (DRY). Returns the matched
 * reason slug or null if the problem type is not session-level.
 */
export function classifySessionForbidden(problemType: string): SessionTerminationReason | null {
  for (const slug of SESSION_FORBIDDEN_SLUGS) {
    if (matchProblemType(problemType, slug)) return slug;
  }
  return null;
}

/** One-shot guard — prevents flooding if multiple requests 401 simultaneously. */
let _sessionExpiredFired = false;

// ─── Cross-Tab Auth Synchronization ──────────────────────────────────────────
// Uses BroadcastChannel to propagate sign-out across all open tabs immediately,
// rather than waiting for the next API call or tab visibility change.
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- avoid static BroadcastChannel reference for Edge Runtime compat
let _authChannel: any = null;

function getAuthChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined') return null;
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!_authChannel) {
    try {
      _authChannel = new BroadcastChannel('virza-auth');
      _authChannel.onmessage = (event: MessageEvent) => {
        if (event.data === 'signed_out') {
          fireSessionExpired();
        }
      };
    } catch {
      // BroadcastChannel not supported (e.g. some older WebViews) — non-fatal
    }
  }
  return _authChannel;
}

// Initialize cross-tab listener eagerly on module load (client-side only)
if (typeof window !== 'undefined') {
  getAuthChannel();
}

/** Broadcast sign-out to all other tabs. Call from signOut(). */
export function broadcastSignOut(): void {
  try {
    getAuthChannel()?.postMessage('signed_out');
  } catch {
    // Non-fatal — other tabs will detect on next API call
  }
}

/** Close the auth BroadcastChannel — call on sign-out to release resources. */
export function closeAuthChannel(): void {
  if (_authChannel) {
    try {
      _authChannel.close();
    } catch {
      // Non-fatal
    }
    _authChannel = null;
  }
}

export function fireSessionExpired(
  reason: SessionTerminationReason = ProblemTypes.SESSION_EXPIRED,
): void {
  if (_sessionExpiredFired || typeof window === 'undefined') return;
  _sessionExpiredFired = true;
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail: { reason } }));
}

// ─── Session Challenge (Soft Enforcement) ────────────────────────────────────
// Suspicious sessions show a persistent banner instead of signing out.
// This matches the Google/GitHub/Slack pattern: reads work, writes blocked.

export const SESSION_CHALLENGE_EVENT = 'virza:session-challenge';

/** Cooldown guard — suppress duplicate challenge events within 30s. */
let _sessionChallengeFiredAt = 0;
const _SESSION_CHALLENGE_COOLDOWN_MS = 30_000;

/**
 * Fire when a 403 with session-suspicious is received on a write path.
 * Shows the SecurityChallengeBanner instead of signing the user out.
 */
export function fireSessionChallenge(): void {
  if (typeof window === 'undefined') return;
  const now = Date.now();
  if (now - _sessionChallengeFiredAt < _SESSION_CHALLENGE_COOLDOWN_MS) return;
  _sessionChallengeFiredAt = now;
  window.dispatchEvent(new CustomEvent(SESSION_CHALLENGE_EVENT));
}

/** Reset the challenge cooldown (for testing). */
export function resetSessionChallengeGuard(): void {
  _sessionChallengeFiredAt = 0;
}

/**
 * Handle a 403 that might be a suspicious-session write block.
 *
 * Returns `true` if the 403 was a session-suspicious problem type (fires
 * the challenge banner), `false` otherwise (caller should handle normally).
 */
export function handleSuspiciousWrite(problemType: string): boolean {
  if (matchProblemType(problemType, ProblemTypes.SESSION_SUSPICIOUS)) {
    fireSessionChallenge();
    return true;
  }
  return false;
}

/** One-shot guard — prevents flooding if multiple workspace requests 403 simultaneously. */
let _workspaceInvalidFired = false;

export function fireWorkspaceInvalid(): void {
  if (_workspaceInvalidFired || typeof window === 'undefined') return;
  _workspaceInvalidFired = true;
  window.dispatchEvent(new CustomEvent(WORKSPACE_INVALID_EVENT));
  // Reset after a short delay so future workspace switches can fire again
  setTimeout(() => {
    _workspaceInvalidFired = false;
  }, 2_000);
}

/**
 * Fire when a 403 insufficient-role is received — triggers workspace list
 * re-fetch so usePermissions() re-evaluates and hides/disables write actions.
 * Throttled (leading edge): the first call dispatches immediately; subsequent
 * calls within the 2s cooldown are suppressed so multiple rapid 403s
 * (e.g. viewer clicking several write actions) coalesce into a single event.
 */
let _permissionsChangedTimer: ReturnType<typeof setTimeout> | null = null;

export function firePermissionsChanged(): void {
  if (typeof window === 'undefined') return;
  if (_permissionsChangedTimer) return; // already scheduled
  _permissionsChangedTimer = setTimeout(() => {
    _permissionsChangedTimer = null;
  }, 2_000);
  window.dispatchEvent(new CustomEvent(PERMISSIONS_CHANGED_EVENT));
}

export interface BillingLimitDetail {
  bucket: string;
  used: number;
  limit: number;
  remaining: number;
  percentUsed: number;
  periodEnd: string;
  upgradePath: string;
}

export function fireBillingLimitExceeded(detail: BillingLimitDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(BILLING_LIMIT_EVENT, { detail }));
}

// ─── Plan Upgrade Required ──────────────────────────────────────────────────

export const PLAN_UPGRADE_EVENT = 'virza:plan-upgrade-required';

// ─── Personal Workspace Upgrade Gate ────────────────────────────────────────

export const PERSONAL_UPGRADE_EVENT = 'virza:personal-upgrade-required';

export interface PersonalUpgradeDetail {
  /** The feature the user was trying to access */
  feature?: string;
  /** The plan the user was trying to upgrade to */
  targetPlan?: string;
}

/**
 * Fire when a user on a personal free workspace attempts to upgrade.
 * ClientShell listens and shows the PersonalUpgradeModal.
 *
 * Deduped within a 300ms window (same pattern as plan-tier 403) so
 * concurrent upgrade attempts don't cause rapid modal state churning.
 */
let _personalUpgradeTimer: ReturnType<typeof setTimeout> | null = null;

export function firePersonalUpgradeRequired(detail: PersonalUpgradeDetail): void {
  if (typeof window === 'undefined') return;
  if (_personalUpgradeTimer) clearTimeout(_personalUpgradeTimer);
  _personalUpgradeTimer = setTimeout(() => {
    _personalUpgradeTimer = null;
    window.dispatchEvent(new CustomEvent(PERSONAL_UPGRADE_EVENT, { detail }));
  }, 300);
}

// ─────────────────────────────────────────────────────────────────────────────

export interface PlanUpgradeDetail {
  /** User's current plan tier (e.g. "free", "starter") */
  currentTier: string;
  /** Minimum plan tier required for the feature (e.g. "pro", "enterprise") */
  requiredTier: string;
  /** Feature key that triggered the gate (e.g. "has_audit_export") */
  feature?: string;
  /** Navigation path to the billing/upgrade page */
  upgradePath: string;
}

/**
 * Extract plan-tier detail fields from an RFC 9457 problem body.
 * Both api-client and typed-client delegate here to avoid duplicating
 * the field extraction and fallback logic.
 */
export function extractPlanTierDetail(body: Record<string, unknown>): PlanUpgradeDetail {
  return {
    currentTier: typeof body.current_tier === 'string' ? body.current_tier : '',
    requiredTier: typeof body.required_tier === 'string' ? body.required_tier : '',
    feature: typeof body.feature === 'string' ? body.feature : undefined,
    upgradePath:
      typeof body.upgrade_path === 'string' ? body.upgrade_path : '/settings/billing/plans',
  };
}

/**
 * Fire when a 403 plan-tier-insufficient response is received.
 * ClientShell listens and shows the PlanUpgradeModal.
 *
 * Deduped within a 300ms window so concurrent 403s from parallel API
 * calls don't cause rapid modal state churning. The last detail wins.
 */
let _planUpgradeTimer: ReturnType<typeof setTimeout> | null = null;

export function firePlanUpgradeRequired(detail: PlanUpgradeDetail): void {
  if (typeof window === 'undefined') return;
  if (_planUpgradeTimer) clearTimeout(_planUpgradeTimer);
  _planUpgradeTimer = setTimeout(() => {
    _planUpgradeTimer = null;
    window.dispatchEvent(new CustomEvent(PLAN_UPGRADE_EVENT, { detail }));
  }, 300);
}

/** Reset after successful login so subsequent 401s fire the event again. */
export function resetSessionExpiredGuard(): void {
  _sessionExpiredFired = false;
}

// ─── Token Refresh ───────────────────────────────────────────────────────────

/**
 * In-flight refresh promise.  Shared across all callers so concurrent 401s
 * don't trigger multiple refreshSession() requests to Supabase.
 */
let _refreshInFlight: Promise<string | null> | null = null;
let _refreshSubscribers = 0;

/**
 * Attempt to refresh the Supabase access token.
 *
 * Deduplicates: if a refresh is already in progress the same promise is
 * returned to all callers.  Returns the new access token on success, null
 * when the refresh token is expired or the user is signed out.
 */
export async function attemptTokenRefresh(): Promise<string | null> {
  if (typeof window === 'undefined') return null;

  // Join existing refresh if one is in flight
  if (_refreshInFlight) {
    _refreshSubscribers++;
    try {
      return await _refreshInFlight;
    } finally {
      _refreshSubscribers--;
      if (_refreshSubscribers === 0) _refreshInFlight = null;
    }
  }

  _refreshSubscribers = 1;
  _refreshInFlight = (async () => {
    try {
      const supabase = getSupabaseBrowserClient();
      // Timeout guard — if Supabase auth server is slow or the network hangs,
      // all concurrent 401 handlers block indefinitely. A 10s timeout unblocks
      // callers so the session-expired flow can fire instead of a silent hang.
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10_000);
      try {
        const { data, error } = await supabase.auth.refreshSession();
        clearTimeout(timeoutId);
        if (error || !data.session?.access_token) {
          return null;
        }
        return data.session.access_token;
      } catch {
        clearTimeout(timeoutId);
        return null;
      }
    } catch {
      return null;
    }
  })();

  try {
    return await _refreshInFlight;
  } finally {
    _refreshSubscribers--;
    if (_refreshSubscribers === 0) _refreshInFlight = null;
  }
}

/**
 * Handle a 401 response from any API client.
 *
 * 1. Attempts a deduplicated token refresh.
 * 2. On success → returns the new access token (caller retries the request).
 * 3. On failure → fires session-expired event, returns null.
 */
export async function handleUnauthorized(): Promise<string | null> {
  const newToken = await attemptTokenRefresh();

  if (!newToken) {
    fireSessionExpired();
    return null;
  }

  return newToken;
}

// ─── Proactive Session Check ─────────────────────────────────────────────────

/**
 * Proactive session health check — call when a backgrounded tab becomes visible.
 *
 * Reads the cached session; if the access token expires within 60 s, triggers
 * an early refresh so the first user action on the tab uses a fresh token.
 * If the refresh token has also expired, fires session-expired.
 */
export async function proactiveSessionCheck(): Promise<void> {
  if (typeof window === 'undefined') return;

  try {
    const supabase = getSupabaseBrowserClient();
    const {
      data: { session },
    } = await supabase.auth.getSession();

    if (!session) {
      // No session at all — user is signed out
      fireSessionExpired();
      return;
    }

    // Refresh proactively if the access token expires within 5 minutes.
    // With 15-min access tokens, a backgrounded tab could return with a
    // 14-min-expired token. Refreshing at 5 min reduces the latency spike.
    const expiresAt = session.expires_at;
    if (expiresAt && expiresAt * 1000 - Date.now() < 300_000) {
      const newToken = await attemptTokenRefresh();
      if (!newToken) {
        fireSessionExpired();
      }
    }
  } catch {
    // Silent fail — the next API call will handle any auth issues
  }
}

// ─── JWT Session ID Extraction ───────────────────────────────────────────────

/**
 * Extract the Supabase session ID (`sid` claim) from a JWT access token.
 *
 * Uses base64 decoding of the payload segment — no cryptographic verification
 * needed since we only read claims from our own locally-held token.
 * The `sid` is used to match incoming Realtime revocation events against
 * the current browser session.
 */
export function getSessionIdFromJwt(accessToken: string): string | null {
  try {
    const parts = accessToken.split('.');
    if (parts.length !== 3) return null;
    // Handle base64url encoding (replace URL-safe chars, add padding)
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(base64));
    return (payload.sid as string) ?? null;
  } catch {
    return null;
  }
}

// ─── Session Validity Heartbeat ──────────────────────────────────────────────

/**
 * Lightweight session validity check — calls `supabase.auth.getUser()` which
 * hits the Supabase auth server (not local cache) to verify the session is
 * still valid. Used as a polling fallback when Realtime WebSocket is unavailable.
 *
 * Returns `true` if the session is still valid, `false` if revoked/expired.
 */
export async function checkSessionValidity(): Promise<boolean> {
  if (typeof window === 'undefined') return true;

  try {
    const supabase = getSupabaseBrowserClient();
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error || !user) {
      fireSessionExpired();
      return false;
    }
    return true;
  } catch {
    // Network error — don't force logout on transient failures
    return true;
  }
}

// ─── Cross-tab Idle Coordination ─────────────────────────────────────────────

const IDLE_ACTIVITY_KEY = 'virza:last-activity';

/** Record user activity timestamp in localStorage for cross-tab visibility. */
export function recordActivity(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(IDLE_ACTIVITY_KEY, Date.now().toString());
  } catch {
    // localStorage unavailable in some incognito modes — non-fatal
  }
}

/** Read the last recorded activity timestamp (any tab). */
export function getLastActivity(): number {
  if (typeof window === 'undefined') return Date.now();
  try {
    const stored = localStorage.getItem(IDLE_ACTIVITY_KEY);
    return stored ? parseInt(stored, 10) : Date.now();
  } catch {
    return Date.now();
  }
}

/**
 * Subscribe to cross-tab activity updates via the `storage` event.
 *
 * Without this, each tab tracks idle time independently — Tab A can log
 * out the user while Tab B is actively being used. The storage event fires
 * in all OTHER tabs when any tab writes to localStorage, keeping all tabs'
 * idle timers in sync.
 *
 * Returns an unsubscribe function. Call from ClientShell on mount.
 */
export function subscribeToActivityUpdates(onActivity: () => void): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = (event: StorageEvent) => {
    if (event.key === IDLE_ACTIVITY_KEY && event.newValue) {
      onActivity();
    }
  };

  window.addEventListener('storage', handler);
  return () => window.removeEventListener('storage', handler);
}
